import { capitalize } from '@crazyegginc/hatch';
import ClickWebP from '@crazyegginc/hatch/dist/images/illustration-button.webp';
import ClickPNG from '@crazyegginc/hatch/dist/images/illustration-button.png';
import UrlVisitWebP from '@crazyegginc/hatch/dist/images/illustration-webpage-tick.webp';
import UrlVisitPNG from '@crazyegginc/hatch/dist/images/illustration-webpage-tick.png';
import FormSubmissionWebP from '@crazyegginc/hatch/dist/images/illustration-form.webp';
import FormSubmissionPNG from '@crazyegginc/hatch/dist/images/illustration-form.png';
import CodeWebP from '@crazyegginc/hatch/dist/images/illustration-javascript-page.webp';
import CodePNG from '@crazyegginc/hatch/dist/images/illustration-javascript-page.png';
import ApiWebP from '@crazyegginc/hatch/dist/images/illustration-api.webp';
import ApiPNG from '@crazyegginc/hatch/dist/images/illustration-api.png';
import GoalWebP from '@crazyegginc/hatch/dist/images/illustration-goal-target.webp';
import GoalPNG from '@crazyegginc/hatch/dist/images/illustration-goal-target.png';
import PixelWebP from '@crazyegginc/hatch/dist/images/illustration-pixel.webp';
import PixelPNG from '@crazyegginc/hatch/dist/images/illustration-pixel.png';
import EcommerceWebP from '@crazyegginc/hatch/dist/images/illustration-ecommerce.webp';
import EcommercePNG from '@crazyegginc/hatch/dist/images/illustration-ecommerce.png';

import { camelToSnake, capitalizeFirstLetter } from '/src/utils/string';
import { GOAL_TRIGGER_TYPES, PIXEL_API_LIST, ECOMMERCE_LIST } from './constants';
import { firstConditionFromFilter, normalizeTriggerType } from './utils';

const NOT_AVAILABLE = 'N/A';

const DEFAULT_TRIGGER_DISPLAY_MODEL = {
  title: 'Other trigger type',
  text: NOT_AVAILABLE,
  urlAddress: '',
  isCode: false,
  codeValue: null,
  summarySegments: [],
};

const humanizePixelApi = (api) => {
  switch (api) {
    case PIXEL_API_LIST.TIKTOK:
      return 'TikTok';
    default:
      return capitalize(api);
  }
};

const humanizeEcommerceApi = (api) => {
  switch (api) {
    case ECOMMERCE_LIST.WIXAPI:
      return 'Wix';
    case ECOMMERCE_LIST.WOOCOMMERCE:
      return 'WooCommerce';
    case ECOMMERCE_LIST.GENERIC:
      return 'Other';
    default:
      return capitalize(api);
  }
};

const formatEcommerceEventLabel = (event) => {
  if (typeof event !== 'string' || event.trim() === '') return NOT_AVAILABLE;
  return capitalizeFirstLetter(camelToSnake(event).replaceAll('_', ' '));
};

const buildEditorSummarySegments = ({ message, value }) => {
  const payload = value || {};
  const page = payload?.on_any_page ? 'any page' : (payload?.on_page ?? NOT_AVAILABLE);
  const wildcard =
    payload?.on_any_page && typeof payload?.on_page === 'string' && payload.on_page.includes('*')
      ? `that matches ${payload.on_page}`
      : null;

  return [message, 'on', page, wildcard].filter(Boolean);
};

export const GOAL_TRIGGERS_METADATA = {
  [GOAL_TRIGGER_TYPES.CLICK]: {
    webp: ClickWebP,
    png: ClickPNG,
    label: (
      <span className="text-xs font-normal">
        When someone <strong className="font-semibold">clicks on a button or link</strong>
      </span>
    ),
    selectLabel: 'Clicks on a button or link',
    buildDisplayModel: (firstValue) => {
      const summarySegments = buildEditorSummarySegments({
        message: 'Clicks a button or a link',
        value: firstValue,
      });

      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Clicks on a button or link',
        text: 'on',
        urlAddress: firstValue?.on_page ?? NOT_AVAILABLE,
        summarySegments,
      };
    },
  },
  [GOAL_TRIGGER_TYPES.URL_VISIT]: {
    webp: UrlVisitWebP,
    png: UrlVisitPNG,
    label: (
      <span className="text-xs font-normal">
        When someone <strong className="font-semibold">reaches a certain page</strong>
      </span>
    ),
    selectLabel: 'Reaches a certain page',
    buildDisplayModel: (firstValue) => {
      const url = firstValue?.url ?? firstValue;

      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Reaches page',
        text: '',
        urlAddress: url ?? NOT_AVAILABLE,
        summarySegments: ['Reaches', url ?? NOT_AVAILABLE],
      };
    },
  },
  [GOAL_TRIGGER_TYPES.FORM_SUBMISSION]: {
    webp: FormSubmissionWebP,
    png: FormSubmissionPNG,
    label: (
      <span className="text-xs font-normal">
        When someone
        <br />
        <strong className="font-semibold">fills out a form</strong>
      </span>
    ),
    selectLabel: 'Fills out a form',
    buildDisplayModel: (firstValue) => {
      const summarySegments = buildEditorSummarySegments({
        message: 'Fills a form',
        value: firstValue,
      });

      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Fills out a form',
        text: 'on',
        urlAddress: firstValue?.on_page ?? NOT_AVAILABLE,
        summarySegments,
      };
    },
  },
  [GOAL_TRIGGER_TYPES.PIXEL_EVENT]: {
    webp: PixelWebP,
    png: PixelPNG,
    label: (
      <span className="text-xs font-normal">
        When someone <strong className="font-semibold">triggers an ad</strong>
        <br />
        <strong className="font-semibold">conversion pixel</strong>
      </span>
    ),
    selectLabel: 'Triggers an ad conversion pixel',
    buildDisplayModel: (firstValue) => {
      const pixelApi = firstValue?.api ? humanizePixelApi(firstValue.api) : NOT_AVAILABLE;

      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Triggers a conversion pixel',
        text: `${pixelApi} event`,
        summarySegments: ['Triggers a conversion pixel'],
      };
    },
  },
  [GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT]: {
    webp: EcommerceWebP,
    png: EcommercePNG,
    label: (
      <span className="text-xs font-normal leading-none">
        When someone completes an
        <br />
        <strong className="font-semibold">eCommerce event</strong>
      </span>
    ),
    selectLabel: 'Completes an eCommerce event',
    buildDisplayModel: (firstValue) => {
      const ecommerceApi = firstValue?.api ? humanizeEcommerceApi(firstValue.api) : NOT_AVAILABLE;

      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Completes an eCommerce event',
        text: `${ecommerceApi} - ${formatEcommerceEventLabel(firstValue?.event)}`,
        summarySegments: ['Completes an ecommerce event'],
      };
    },
  },
  [GOAL_TRIGGER_TYPES.CODE]: {
    webp: CodeWebP,
    png: CodePNG,
    label: (
      <span className="text-xs font-normal">
        When someone <strong className="font-semibold">triggers a custom HTML tag</strong> (advanced)
      </span>
    ),
    selectLabel: 'Triggers a Custom HTML Tag',
    buildDisplayModel: (firstValue) => {
      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Triggers a Custom HTML Tag',
        text: '',
        isCode: true,
        codeValue: firstValue ?? null,
        summarySegments: ['Triggers a Custom HTML Tag'],
      };
    },
  },
  [GOAL_TRIGGER_TYPES.API]: {
    webp: ApiWebP,
    png: ApiPNG,
    label: (
      <span className="text-xs font-normal">
        When it gets <strong className="font-semibold">called via API</strong>
        <br />
        <span className="text-lynch-500">(advanced)</span>
      </span>
    ),
    selectLabel: 'Called via API',
    info: 'This trigger was added automatically when you called our API. You can not edit it. To delete it (1) stop sending us this event via our API, and then (2) delete the Goal.',
    buildDisplayModel: () => {
      return {
        ...DEFAULT_TRIGGER_DISPLAY_MODEL,
        title: 'Called via API',
        text: '',
        summarySegments: ['Called via API'],
      };
    },
  },
  OTHER: {
    webp: GoalWebP,
    png: GoalPNG,
    selectLabel: 'Custom trigger',
    buildDisplayModel: () => ({ ...DEFAULT_TRIGGER_DISPLAY_MODEL }),
  },
};

export function getGoalTriggerMetadata(triggerType) {
  if (typeof triggerType !== 'string') {
    return GOAL_TRIGGERS_METADATA.OTHER;
  }

  return GOAL_TRIGGERS_METADATA[triggerType.toUpperCase()] ?? GOAL_TRIGGERS_METADATA.OTHER;
}

export function getGoalTriggerDisplayModel(trigger) {
  const type = normalizeTriggerType(trigger?.type ?? trigger?.trigger);
  const condition = firstConditionFromFilter(trigger?.filter);
  const criteriaType = normalizeTriggerType(condition?.criteria);
  const resolvedType = type || criteriaType;
  const values = Array.isArray(condition?.values) ? condition.values : [];
  const firstValue = values[0];
  const metadata = getGoalTriggerMetadata(resolvedType);

  return metadata?.buildDisplayModel?.(firstValue) ?? { ...DEFAULT_TRIGGER_DISPLAY_MODEL };
}
